import {
    CSSProperties,
    MaybeRefOrGetter,
    onWatcherCleanup,
    shallowReadonly,
    shallowRef,
    toValue,
    watchEffect,
} from "vue";
import { takeWhile } from "@/types/array";
import { ShallowRef } from "@vue/runtime-core";
import { useResizeObserver } from "@vueuse/core";

export type SizeDimension = "width" | "height" | "innerWidth" | "innerHeight";

export function useSizeBreakpoint<
    const B extends string,
    const T extends [number, B][],
>(
    element: MaybeRefOrGetter<HTMLElement | null>,
    dimension: SizeDimension,
    breakpoints: T,
): Readonly<ShallowRef<B>> {
    const outRef = shallowRef<B>(breakpoints[0][1]);

    const sizeMetric = {
        width: (e: Element | null) => e?.getBoundingClientRect?.()?.width,
        height: (e: Element | null) => e?.getBoundingClientRect?.()?.height,
        innerWidth: (e: Element | null) => e?.clientWidth,
        innerHeight: (e: Element | null) => e?.clientHeight,
    }[dimension];

    const updateSize = (size: number | undefined) => {
        if (typeof size === "undefined") {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            outRef.value = takeWhile(([bp]) => bp < size, breakpoints)
                .map(([, name]) => name)
                .at(-1);
        });

        onWatcherCleanup(() => {
            cancelAnimationFrame(frameId);
        });
    };

    watchEffect(
        () => {
            updateSize(sizeMetric(toValue(element)));
        },
        { flush: "post" },
    );

    useResizeObserver(element, (entries) => {
        entries.forEach((v) => {
            requestAnimationFrame(() => {
                updateSize(sizeMetric(v.target));
            });
        });
    });

    return shallowReadonly(outRef);
}

export type RectBox = {
    x: number;
    y: number;
    width: number;
    height: number;
};

export function insetRect(rect: RectBox, inset: number): RectBox {
    const xMid = (rect.x + rect.width) / 2.0;
    const yMid = (rect.y + rect.height) / 2.0;
    return {
        x: Math.min(rect.x + inset, xMid),
        y: Math.min(rect.y + inset, yMid),
        width: Math.max(rect.width - inset * 2.0, xMid),
        height: Math.max(rect.height - inset * 2.0, yMid),
    };
}

function rangeOverlap(
    [innerStart, innerEnd]: [number, number],
    [outerStart, outerEnd]: [number, number],
): number {
    if (innerEnd - innerStart < 0.5 || outerEnd - outerStart < 0.5) {
        return 0;
    } else if (innerStart >= outerStart && innerEnd <= outerEnd) {
        return 1;
    } else if (outerStart > innerStart && outerEnd < innerEnd) {
        return (outerEnd - outerStart) / (innerEnd - innerStart);
    } else if (innerEnd < outerStart || innerStart > outerEnd) {
        return 0;
    } else if (outerStart <= innerStart) {
        return (outerEnd - innerStart) / (outerEnd - outerStart);
    } else {
        return (innerEnd - outerStart) / (outerEnd - outerStart);
    }
}

export function containment(outer: RectBox, inner: RectBox): number {
    return (
        rangeOverlap(
            [inner.x, inner.x + inner.width],
            [outer.x, outer.x + outer.width],
        ) *
        rangeOverlap(
            [inner.y, inner.y + inner.height],
            [outer.y, outer.y + outer.height],
        )
    );
}

function placeRectBase({
    anchorBox,
    innerBox,
    position,
    align,
}: {
    anchorBox: RectBox;
    innerBox: RectBox;
    position: "top" | "bottom" | "left" | "right";
    align: "start" | "center" | "end";
}) {
    const placement = {
        top() {
            return {
                ...innerBox,
                x: anchorBox.x,
                y: anchorBox.y - innerBox.height,
            };
        },
        right() {
            return {
                ...innerBox,
                x: anchorBox.x + anchorBox.width,
                y: anchorBox.y,
            };
        },
        bottom() {
            return {
                ...innerBox,
                x: anchorBox.x,
                y: anchorBox.y + anchorBox.height,
            };
        },
        left() {
            return {
                ...innerBox,
                x: anchorBox.x - innerBox.width,
                y: anchorBox.y,
            };
        },
    }[position]();

    if (align === "center") {
        if (position === "top" || position === "bottom") {
            placement.x -= (innerBox.width - anchorBox.width) / 2.0;
        } else {
            placement.y -= (innerBox.height - anchorBox.height) / 2.0;
        }
    } else if (align === "end") {
        if (position === "top" || position === "bottom") {
            placement.x = anchorBox.width - innerBox.width;
        } else {
            placement.y = anchorBox.height - innerBox.height;
        }
    }

    return placement;
}

function flipPosition(position: "top" | "bottom" | "left" | "right") {
    switch (position) {
        case "top":
            return "bottom";
        case "right":
            return "left";
        case "bottom":
            return "top";
        case "left":
            return "right";
    }
}

function placeRectCorrection({
    innerBox,
    outerBox,
}: {
    innerBox: RectBox;
    outerBox: RectBox;
    position: "top" | "bottom" | "left" | "right";
    align: "start" | "center" | "end";
}): RectBox {
    const box = { ...innerBox };

    if (outerBox.width < innerBox.width) {
        innerBox.width = outerBox.width;
    }

    if (outerBox.height < innerBox.height) {
        innerBox.height = outerBox.height;
    }

    return {
        ...box,
        x: Math.min(
            Math.max(box.x, outerBox.x),
            outerBox.x + outerBox.width - box.width,
        ),
        y: Math.min(
            Math.max(box.y, outerBox.y),
            outerBox.y + outerBox.height - box.height,
        ),
    };
}

function rectTransformPoint({
    anchorBox,
    innerBox,
}: {
    anchorBox: RectBox;
    innerBox: RectBox;
}): CSSProperties["transformOrigin"] & string {
    const [srcX, srcY] = [
        anchorBox.x + anchorBox.width / 2.0,
        anchorBox.y + anchorBox.height / 2.0,
    ];

    const [midX, midY] = [innerBox.width / 2.0, innerBox.height / 2.0];

    const [dstX, dstY] = [innerBox.x + midX, innerBox.y + midY];

    const [clampedDX, clampedDY] = [
        Math.min(Math.max(srcX - dstX, -midX), midX),
        Math.min(Math.max(srcY - dstY, -midY), midY),
    ];

    return `${midX + clampedDX}px ${midY + clampedDY}px`;
}

export function placeRectBox({
    anchorBox,
    innerBox,
    containingBox,
    position,
    align,
    inset,
}: {
    anchorBox: RectBox;
    innerBox: RectBox;
    containingBox: RectBox;
    position: "top" | "bottom" | "left" | "right";
    align: "start" | "center" | "end";
    inset: number;
}): {
    transformOrigin: CSSProperties["transformOrigin"] & string;
    top: string;
    left: string;
} {
    const safeBox = insetRect(containingBox, inset);

    const placement = placeRectBase({
        anchorBox,
        innerBox,
        position,
        align,
    });

    const altPlacement = placeRectBase({
        anchorBox,
        innerBox,
        position: flipPosition(position),
        align,
    });

    if (containment(safeBox, placement) < containment(safeBox, altPlacement)) {
        const bestPlacement = placeRectCorrection({
            innerBox: altPlacement,
            outerBox: safeBox,
            position: flipPosition(position),
            align,
        });

        return {
            left: bestPlacement.x + "px",
            top: bestPlacement.y + "px",
            transformOrigin: rectTransformPoint({
                anchorBox,
                innerBox: bestPlacement,
            }),
        };
    }

    const bestPlacement = placeRectCorrection({
        innerBox: placement,
        outerBox: safeBox,
        position,
        align,
    });

    return {
        left: bestPlacement.x + "px",
        top: bestPlacement.y + "px",
        transformOrigin: rectTransformPoint({
            anchorBox,
            innerBox: bestPlacement,
        }),
    };
}
